/**
 * AiChatPanel — Task 4.5
 * Floating chat panel for healers. General medical knowledge only.
 * Scope: anatomy, physiology, common conditions, wellness — never MCKS or patient-specific.
 * History trimmed to last 10 turns in backend — not persisted in DB or localStorage.
 * Plan gate enforced at backend (STARTER+). Frontend shows quota info from response.
 */

import React, { useState, useRef, useEffect } from 'react';
import { chatWithAI } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

const BRAND_GREEN = '#4a8f82';

export default function AiChatPanel() {
  const { isMobile } = useWindowSize();
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [history, setHistory] = useState([]); // [{ role, content }]
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [quota, setQuota]     = useState(null); // { used, limit }
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, open]);

  async function handleSend() {
    const msg = input.trim();
    if (!msg || loading) return;

    const newHistory = [...history, { role: 'user', content: msg }];
    setHistory(newHistory);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Send only role+content to backend — strip any display-only fields
      const sendHistory = newHistory.slice(0, -1); // all but the message we just added
      const res = await chatWithAI({ message: msg, history: sendHistory });
      const { reply, queriesUsedThisMonth, queriesLimit } = res.data.data;
      setHistory(h => [...h, { role: 'assistant', content: reply }]);
      setQuota({ used: queriesUsedThisMonth, limit: queriesLimit });
    } catch (err) {
      const serverMsg = err.response?.data?.message;
      const code      = err.response?.data?.code;
      if (code === 'CHAT_QUOTA_EXCEEDED') {
        setError('Monthly limit reached. Upgrade to AI Admin for unlimited access.');
      } else if (code === 'PLAN_UPGRADE_REQUIRED') {
        setError('AI Chatbot requires STARTER plan or above.');
      } else {
        setError(serverMsg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleClear() {
    setHistory([]);
    setError(null);
    setQuota(null);
  }

  const panelWidth  = isMobile ? '100vw' : '380px';
  const panelRight  = isMobile ? '0'     : '24px';
  const panelBottom = isMobile ? '0'     : '88px';
  const panelHeight = isMobile ? '100vh' : '520px';
  const panelRadius = isMobile ? '0'     : '12px';

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close AI Chat' : 'Open AI Medical Chat'}
        style={styles.fab}
      >
        {open ? '✕' : '💬'}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          ...styles.panel,
          width:        panelWidth,
          right:        panelRight,
          bottom:       panelBottom,
          height:       panelHeight,
          borderRadius: panelRadius
        }}>
          {/* Header */}
          <div style={styles.header}>
            <div>
              <div style={styles.headerTitle}>AI Medical Assistant</div>
              <div style={styles.headerSub}>General anatomy, physiology &amp; wellness</div>
            </div>
            <button onClick={handleClear} style={styles.clearBtn} title="Clear conversation">
              Clear
            </button>
          </div>

          {/* Quota bar */}
          {quota?.limit !== null && quota !== null && (
            <div style={styles.quotaBar}>
              {quota.used}/{quota.limit} queries used this month
            </div>
          )}

          {/* Messages */}
          <div style={styles.messages}>
            {history.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🩺</div>
                <div style={styles.emptyTitle}>Medical Knowledge Assistant</div>
                <div style={styles.emptyHint}>
                  Ask about anatomy, physiology, common health conditions, or general wellness.
                </div>
                <div style={styles.emptyWarning}>
                  Not for patient-specific advice or MCKS technique guidance.
                </div>
              </div>
            )}

            {history.map((msg, i) => (
              <div key={i} style={msg.role === 'user' ? styles.userMsgWrap : styles.asstMsgWrap}>
                <div style={msg.role === 'user' ? styles.userBubble : styles.asstBubble}>
                  <MessageContent content={msg.content} />
                </div>
              </div>
            ))}

            {loading && (
              <div style={styles.asstMsgWrap}>
                <div style={styles.asstBubble}>
                  <span style={styles.typing}>Thinking…</span>
                </div>
              </div>
            )}

            {error && (
              <div style={styles.errorBanner}>{error}</div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input row */}
          <div style={styles.inputRow}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a medical question… (Enter to send)"
              rows={2}
              style={styles.textarea}
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={{
                ...styles.sendBtn,
                opacity: (loading || !input.trim()) ? 0.5 : 1
              }}
              aria-label="Send message"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Renders markdown-like disclaimer separator as styled block
function MessageContent({ content }) {
  const parts = content.split('\n\n---\n');
  if (parts.length === 1) return <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>;
  return (
    <>
      <span style={{ whiteSpace: 'pre-wrap' }}>{parts[0]}</span>
      <div style={styles.disclaimerBlock}>{parts[1].replace(/\*/g, '')}</div>
    </>
  );
}

const styles = {
  fab: {
    position:     'fixed',
    bottom:       '24px',
    right:        '24px',
    width:        '56px',
    height:       '56px',
    borderRadius: '50%',
    background:   BRAND_GREEN,
    color:        '#fff',
    border:       'none',
    fontSize:     '22px',
    cursor:       'pointer',
    boxShadow:    '0 4px 16px rgba(74,143,130,0.45)',
    zIndex:       1000,
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center'
  },
  panel: {
    position:      'fixed',
    zIndex:        999,
    background:    '#fff',
    boxShadow:     '0 8px 32px rgba(0,0,0,0.18)',
    display:       'flex',
    flexDirection: 'column',
    overflow:      'hidden'
  },
  header: {
    background:     BRAND_GREEN,
    color:          '#fff',
    padding:        '14px 16px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    flexShrink:     0
  },
  headerTitle: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: '700',
    fontSize:   '14px',
    letterSpacing: '0.5px'
  },
  headerSub: {
    fontSize: '11px',
    opacity:  0.8,
    marginTop: '2px'
  },
  clearBtn: {
    background: 'rgba(255,255,255,0.2)',
    border:     '1px solid rgba(255,255,255,0.4)',
    color:      '#fff',
    borderRadius: '4px',
    padding:    '5px 12px',
    fontFamily: "'Raleway', sans-serif",
    fontSize:   '11px',
    fontWeight: '700',
    cursor:     'pointer',
    minWidth:   '44px',
    minHeight:  '44px'
  },
  quotaBar: {
    background: '#f0faf8',
    borderBottom: '1px solid #d4ede8',
    padding:    '6px 16px',
    fontSize:   '11px',
    color:      '#4a8f82',
    fontWeight: '600',
    flexShrink: 0
  },
  messages: {
    flex:       1,
    overflowY:  'auto',
    padding:    '16px',
    display:    'flex',
    flexDirection: 'column',
    gap:        '10px'
  },
  emptyState: {
    textAlign:  'center',
    padding:    '32px 16px',
    color:      '#888'
  },
  emptyIcon: {
    fontSize:   '36px',
    marginBottom: '10px'
  },
  emptyTitle: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: '700',
    fontSize:   '14px',
    color:      '#333',
    marginBottom: '8px'
  },
  emptyHint: {
    fontSize:   '12px',
    color:      '#666',
    lineHeight: '1.6',
    marginBottom: '12px'
  },
  emptyWarning: {
    fontSize:     '11px',
    color:        '#e67e22',
    background:   '#fef9f0',
    border:       '1px solid #fce5c0',
    borderRadius: '6px',
    padding:      '8px 12px',
    lineHeight:   '1.5'
  },
  userMsgWrap: {
    display:        'flex',
    justifyContent: 'flex-end'
  },
  asstMsgWrap: {
    display:        'flex',
    justifyContent: 'flex-start'
  },
  userBubble: {
    background:   BRAND_GREEN,
    color:        '#fff',
    borderRadius: '12px 12px 2px 12px',
    padding:      '10px 14px',
    maxWidth:     '80%',
    fontSize:     '13px',
    lineHeight:   '1.6'
  },
  asstBubble: {
    background:   '#f5f5f5',
    color:        '#333',
    borderRadius: '12px 12px 12px 2px',
    padding:      '10px 14px',
    maxWidth:     '85%',
    fontSize:     '13px',
    lineHeight:   '1.6'
  },
  typing: {
    color:       '#999',
    fontStyle:   'italic',
    fontSize:    '12px'
  },
  errorBanner: {
    background:   '#fef2f2',
    border:       '1px solid #fecaca',
    borderRadius: '6px',
    padding:      '10px 14px',
    fontSize:     '12px',
    color:        '#b91c1c'
  },
  disclaimerBlock: {
    marginTop:    '10px',
    fontSize:     '11px',
    color:        '#888',
    borderTop:    '1px solid #ddd',
    paddingTop:   '8px',
    lineHeight:   '1.5',
    fontStyle:    'italic'
  },
  inputRow: {
    display:      'flex',
    gap:          '8px',
    padding:      '12px',
    borderTop:    '1px solid #e0e0e0',
    background:   '#fafafa',
    flexShrink:   0,
    alignItems:   'flex-end'
  },
  textarea: {
    flex:         1,
    resize:       'none',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    padding:      '10px 12px',
    fontSize:     '16px', // prevents iOS auto-zoom
    fontFamily:   "'Open Sans', sans-serif",
    lineHeight:   '1.5',
    outline:      'none',
    color:        '#333'
  },
  sendBtn: {
    background:   BRAND_GREEN,
    color:        '#fff',
    border:       'none',
    borderRadius: '6px',
    padding:      '0 18px',
    fontFamily:   "'Raleway', sans-serif",
    fontWeight:   '700',
    fontSize:     '13px',
    cursor:       'pointer',
    minHeight:    '44px',
    minWidth:     '60px'
  }
};
